import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useAppUpdate } from "@/hooks/useAppUpdate";
import { Checkbox } from "@/components/ui/checkbox";
import { logEvent } from "@/lib/mobile-diagnostics";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const AppUpdateDialog = () => {
  const { updateAvailable, storeVersion, openStore, dismissUpdate } = useAppUpdate();
  const [open, setOpen] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!updateAvailable || !storeVersion) {
      setOpen(false);
      return;
    }
    setDontAskAgain(false);
    setOpen(true);
    logEvent({
      category: "update",
      event_type: "dialog_shown",
      context: { store_version: storeVersion },
    });
  }, [updateAvailable, storeVersion]);

  if (!Capacitor.isNativePlatform()) return null;

  const handleUpdate = () => {
    setOpen(false);
    logEvent({
      category: "update",
      event_type: "store_opened",
      context: { store_version: storeVersion },
    });
    openStore();
  };

  const handleLater = () => {
    if (dontAskAgain && storeVersion) {
      dismissUpdate(storeVersion);
    }
    logEvent({
      category: "update",
      event_type: "dialog_dismissed",
      context: { store_version: storeVersion, dont_ask_again: dontAskAgain },
    });
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-[90vw] sm:max-w-md mx-auto rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Update Available</AlertDialogTitle>
          <AlertDialogDescription>
            A new version of Decor Canvas
            {storeVersion ? ` (v${storeVersion})` : ""} is available. Update now
            to get the latest features and fixes.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
          <Checkbox
            checked={dontAskAgain}
            onCheckedChange={(v) => setDontAskAgain(v === true)}
            className="data-[state=checked]:bg-[hsl(15,65%,55%)] data-[state=checked]:border-[hsl(15,65%,55%)]"
          />
          <span className="text-sm text-muted-foreground">
            Don't ask again for this version
          </span>
        </label>

        <AlertDialogFooter className="flex-row justify-end gap-2">
          <AlertDialogCancel onClick={handleLater} className="mt-0">
            Later
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleUpdate}
            className="bg-[hsl(15,65%,55%)] hover:bg-[hsl(15,65%,50%)]"
          >
            Update
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
